import { FC, useState, useEffect, useRef } from 'react';
import {
    Bar, BarChart, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from '../services/api';
import type { TooltipProps } from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

// --- Icons ---
const Icons = {
    Calendar: <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    Download: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>,
    FileText: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    Grid: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>,
    Chart: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" /></svg>,
    Trophy: <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
};

interface ReportData {
    scansByDayOfWeek: { day: string; count: number }[];
    scansByHour: { hour: number; count: number }[];
    scansByCategory: { categoryName: string; count: number }[];
    topQrCodes: { qrCodeName: string; count: number }[];
    scanResults: { status: boolean; count: number }[];
}

const ReportsPage: FC = () => {
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Inizializza con data e ora locale (inizio giornata e fine giornata)
    const getLocalISOString = (date: Date) => {
        const offset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - offset).toISOString().slice(0, 16);
    };

    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        d.setHours(0, 0, 0, 0);
        return getLocalISOString(d);
    });

    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return getLocalISOString(d);
    });

    const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

    const exportMenuRef = useRef<HTMLDivElement>(null);

    const fetchReports = async (start: string, end: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await api.get('/api/scans/report', {
                params: { startDate: start, endDate: end }
            });
            if (response.data.status) {
                setReportData(response.data.data);
            } else {
                throw new Error(response.data.message || "Errore nel caricamento dei report.");
            }
        } catch (err: any) {
            setError(err.message || "Errore di rete.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReports(startDate, endDate);
    }, [startDate, endDate]);

    const handleGenerateReport = async (format: 'pdf' | 'csv' | 'excel') => {
        setIsExportMenuOpen(false);
        setIsGenerating(true);
        setError(null);
        try {
            const response = await api.get('/api/scans/download', {
                params: { startDate, endDate, format },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const extension = format === 'excel' ? 'xlsx' : format;

            // Formatta il nome file rimuovendo i caratteri speciali
            const safeStart = startDate.replace('T', '_').replaceAll(':', '');
            const safeEnd = endDate.replace('T', '_').replaceAll(':', '');
            const filename = `report_scansioni_${safeStart}_al_${safeEnd}.${extension}`;

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            setError(`Errore durante la generazione del report ${format.toUpperCase()}.`);
        } finally {
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
                setIsExportMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const PIE_COLORS = ['#0ea5e9', '#a855f7', '#f59e0b', '#10b981', '#f43f5e', '#64748b'];

    interface CustomTooltipProps extends TooltipProps<ValueType, NameType> {
        payload?: { value: number }[];
        label?: string;
    }

    const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
        if (active && payload && payload.length) {
            return (
                <div className="p-3 bg-slate-900/90 backdrop-blur border border-slate-700 rounded-lg shadow-xl">
                    <p className="text-slate-400 text-xs mb-1 font-semibold uppercase tracking-wider">{label ? `Ore ${label}:00` : ''}</p>
                    <p className="text-white font-bold text-lg">
                        <span className="text-sky-400 mr-2">●</span>
                        {payload[0].value} Scansioni
                    </p>
                </div>
            );
        }
        return null;
    };

    const scanResultData = reportData?.scanResults.map(r => ({
        name: r.status ? 'Valide' : 'Non Valide',
        value: r.count
    })) || [];

    const handleGenerateSummary = async (format: 'pdf' | 'excel') => {
        setIsGeneratingSummary(true);
        setError(null);
        try {
            const response = await api.get('/api/scans/summary/download', {
                params: { format },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const extension = format === 'excel' ? 'xlsx' : 'pdf';
            const filename = `riepilogo_qrcode_completo.${extension}`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            setError(`Errore durante la generazione del riepilogo ${format.toUpperCase()}.`);
        } finally {
            setIsGeneratingSummary(false);
        }
    };

    return (
        <div className="space-y-8 animate-fade-in pb-12">
            {/* Header Toolbar */}
            <div className="relative z-30 flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6 bg-slate-800/40 backdrop-blur-md p-6 rounded-2xl border border-slate-700/50 shadow-lg">
                <div>
                    <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                        Report e Analisi
                    </h1>
                    <p className="text-slate-400 mt-2 text-lg">
                        Visualizza le performance e genera report dettagliati.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 w-full xl:w-auto">
                    {/* Date Picker Range (Ora in datetime-local) */}
                    <div className="flex items-center bg-slate-900/50 p-1.5 rounded-xl border border-slate-700/50 w-full sm:w-auto">
                        <div className="relative group flex-1 sm:flex-none">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-sky-400">
                                {Icons.Calendar}
                            </div>
                            <input
                                type="datetime-local"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="bg-transparent border-none text-slate-200 text-sm rounded-lg focus:ring-0 block pl-10 p-2.5 w-full sm:w-auto [color-scheme:dark]"
                            />
                        </div>
                        <span className="text-slate-500 px-2 font-medium">→</span>
                        <div className="relative group flex-1 sm:flex-none">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-500 group-focus-within:text-sky-400">
                                {Icons.Calendar}
                            </div>
                            <input
                                type="datetime-local"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="bg-transparent border-none text-slate-200 text-sm rounded-lg focus:ring-0 block pl-10 p-2.5 w-full sm:w-auto [color-scheme:dark]"
                            />
                        </div>
                    </div>

                    {/* Export Dropdown */}
                    <div className="relative w-full sm:w-auto" ref={exportMenuRef}>
                        <button
                            onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                            disabled={isGenerating}
                            className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 shadow-lg shadow-sky-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed active:scale-95"
                        >
                            {isGenerating ? (
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            ) : (
                                Icons.Download
                            )}
                            <span>Esporta Dati</span>
                        </button>

                        {isExportMenuOpen && (
                            <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-xl bg-slate-800 border border-slate-700 shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                <div className="py-1">
                                    <button onClick={() => handleGenerateReport('pdf')} className="flex w-full items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
                                        <span className="w-8 text-center mr-2 bg-red-500/10 text-red-400 rounded py-0.5 text-xs font-bold">PDF</span> Report PDF
                                    </button>
                                    <button onClick={() => handleGenerateReport('excel')} className="flex w-full items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
                                        <span className="w-8 text-center mr-2 bg-emerald-500/10 text-emerald-400 rounded py-0.5 text-xs font-bold">XLS</span> Excel
                                    </button>
                                    <button onClick={() => handleGenerateReport('csv')} className="flex w-full items-center px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors">
                                        <span className="w-8 text-center mr-2 bg-slate-500/10 text-slate-400 rounded py-0.5 text-xs font-bold">CSV</span> CSV
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-32 text-slate-400">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500 mb-4"></div>
                    <p className="text-lg">Analisi dei dati in corso...</p>
                </div>
            ) : error ? (
                <div className="p-6 bg-red-900/20 border border-red-800/50 backdrop-blur text-red-200 rounded-2xl flex items-center gap-4">
                    <svg className="w-8 h-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <div>
                        <h3 className="font-bold text-lg">Si è verificato un errore</h3>
                        <p>{error}</p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* Chart: Scans By Hour */}
                    <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 shadow-xl flex flex-col min-h-[450px]">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-sky-500/10 rounded-lg text-sky-400">{Icons.Grid}</div>
                            <h3 className="text-lg font-bold text-white">Scansioni per Fascia Oraria</h3>
                        </div>
                        <div className="flex-1 w-full h-full min-h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={reportData?.scansByHour} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                    <defs>
                                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                    <XAxis
                                        dataKey="hour"
                                        stroke="#94a3b8"
                                        tickFormatter={(hour) => `${hour}:00`}
                                        tick={{fill: '#94a3b8', fontSize: 12}}
                                        axisLine={false}
                                        tickLine={false}
                                        dy={10}
                                    />
                                    <YAxis
                                        stroke="#94a3b8"
                                        tick={{fill: '#94a3b8', fontSize: 12}}
                                        axisLine={false}
                                        tickLine={false}
                                        dx={-10}
                                    />
                                    <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(51, 65, 85, 0.4)'}}/>
                                    <Bar
                                        dataKey="count"
                                        fill="url(#colorCount)"
                                        radius={[6, 6, 0, 0]}
                                        barSize={32}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart: Categories Pie */}
                    <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 shadow-xl flex flex-col min-h-[450px]">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-purple-500/10 rounded-lg text-purple-400">{Icons.Chart}</div>
                            <h3 className="text-lg font-bold text-white">Ripartizione Categorie</h3>
                        </div>
                        <div className="flex-1 w-full h-full min-h-[300px] relative">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={reportData?.scansByCategory}
                                        dataKey="count"
                                        nameKey="categoryName"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={120}
                                        paddingAngle={5}
                                        stroke="none"
                                    >
                                        {reportData?.scansByCategory.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '0.5rem', color: '#fff' }}
                                        itemStyle={{ color: '#fff' }}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        iconType="circle"
                                        formatter={(value) => <span className="text-slate-300 ml-1">{value}</span>}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Center Text */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none mb-8">
                                <div className="text-center">
                                    <span className="block text-3xl font-bold text-white">
                                        {reportData?.scansByCategory.reduce((acc, curr) => acc + curr.count, 0)}
                                    </span>
                                    <span className="text-xs text-slate-500 uppercase font-semibold">Totali</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top QR Codes List */}
                    <div className="lg:col-span-1 bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-amber-500/10 rounded-lg text-amber-400">{Icons.Trophy}</div>
                            <h3 className="text-lg font-bold text-white">Top 5 QR Code</h3>
                        </div>
                        <div className="space-y-4">
                            {reportData?.topQrCodes.map((qr, index) => (
                                <div key={index} className="flex items-center justify-between p-4 bg-slate-900/40 rounded-xl border border-slate-700/30 hover:bg-slate-900/60 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className={`
                                            w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                                            ${index === 0 ? 'bg-yellow-500 text-yellow-900' :
                                            index === 1 ? 'bg-slate-400 text-slate-900' :
                                                index === 2 ? 'bg-orange-700 text-orange-200' : 'bg-slate-700 text-slate-400'}
                                        `}>
                                            {index + 1}
                                        </div>
                                        <span className="font-medium text-slate-200 truncate max-w-[180px]">{qr.qrCodeName}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl font-bold text-white">{qr.count}</span>
                                        <span className="text-xs text-slate-500 uppercase font-bold mt-1">Scan</span>
                                    </div>
                                </div>
                            ))}
                            {reportData?.topQrCodes.length === 0 && (
                                <p className="text-center text-slate-500 py-8">Nessun dato disponibile.</p>
                            )}
                        </div>
                    </div>

                    {/* Scan Results Pie (Valid vs Invalid) */}
                    <div className="lg:col-span-1 bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-400">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <h3 className="text-lg font-bold text-white">Validità Scansioni</h3>
                        </div>
                        <div className="flex items-center justify-center h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={scanResultData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        stroke="none"
                                        label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
                                            const RADIAN = Math.PI / 180;
                                            const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                                            return percent > 0 ? `${(percent * 100).toFixed(0)}%` : '';
                                        }}
                                        labelLine={false}
                                    >
                                        <Cell fill="#f43f5e" /> {/* Invalid - Red */}
                                        <Cell fill="#10b981" /> {/* Valid - Green */}
                                    </Pie>
                                    <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155', borderRadius: '0.5rem', color: '#fff' }} />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        iconType="circle"
                                        formatter={(value) => <span className="text-slate-300 ml-1">{value}</span>}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom: Summary Section */}
            <div className="mt-8 p-1 rounded-3xl bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700/50 shadow-2xl">
                <div className="bg-slate-900/90 backdrop-blur rounded-[20px] p-8 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div>
                        <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                            <span className="p-2 bg-indigo-500/20 rounded-lg text-indigo-300">{Icons.FileText}</span>
                            Riepilogo di Fine Ciclo
                        </h2>
                        <p className="text-slate-400 max-w-2xl">
                            Scarica un documento completo con lo stato attuale di tutti i QR code.
                            Utile per archiviazione o prima di effettuare pulizie del database.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-4 flex-shrink-0 w-full md:w-auto">
                        <button
                            onClick={() => handleGenerateSummary('pdf')}
                            disabled={isGeneratingSummary}
                            className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold bg-slate-800 hover:bg-slate-700 border border-slate-600 hover:border-slate-500 transition-all flex items-center justify-center gap-2"
                        >
                            <span className="text-red-400 font-bold text-xs bg-red-400/10 px-1.5 py-0.5 rounded">PDF</span>
                            <span>{isGeneratingSummary ? '...' : 'Scarica PDF'}</span>
                        </button>
                        <button
                            onClick={() => handleGenerateSummary('excel')}
                            disabled={isGeneratingSummary}
                            className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2"
                        >
                            <span className="text-white font-bold text-xs bg-white/20 px-1.5 py-0.5 rounded">XLS</span>
                            <span>{isGeneratingSummary ? 'Generazione...' : 'Scarica Excel'}</span>
                        </button>
                    </div>
                </div>
            </div>
            {error && isGeneratingSummary && (
                <div className="mt-4 p-4 bg-red-900/50 border border-red-700 text-red-200 rounded-xl text-center">
                    {error}
                </div>
            )}
        </div>
    );
};

export default ReportsPage;